import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import { keys } from '../../api/keys'
import { emit } from '../../platform/analytics/analytics'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import type { Txn } from '../transactions/transactionWire'
import { getTransaction } from '../transactions/useTransaction'
import { invalidateAfterRuleWrite, RulesError, type Batch } from './useRules'

/** The most one Apply now writes. The server allows 1–5000. */
export const APPLY_LIMIT = 5000

/** Refresh the rules list after a 404 on one rule. The list only: the `rules` root also holds the
 *  dry run that 404'd, and refetching it would ask again. */
const ruleGone = (client: QueryClient) => client.invalidateQueries({ queryKey: keys.rules.list() })

export type ApplyCall =
  | { kind: 'selected'; transactionIds: string[]; overwrite: boolean }
  /** `resumeAfter`: the cursor a run cut short returned, to continue from where it stopped (Continue);
   *  `null` for a first run. The window and the overwrite choice stay those of the first run. */
  | { kind: 'window'; from: string; overwrite: boolean; resumeAfter: string | null }

/** An apply's outcome, in the screen's terms. */
export type Applied = {
  batchId: string
  /** Exact: what this run wrote. */
  modified: number
  /** The server's `truncated`: it stopped before the end of the window (or the chosen ids). */
  truncated: boolean
  /** What it evaluated before it stopped, matched or not. */
  processed: number
  /** Left alone because the user edited the field by hand (`respect_user_edits`). */
  skippedLocked: number
  /** The window the server actually used, when it ran over one. */
  from: string | null
  /** Where a run cut short stopped, to send back as `resume_after` (Continue). `null` when it ran to
   *  the end, or the server gave no cursor. */
  resumeAfter: string | null
}

const appliedOf = (b: Batch): Applied => ({
  batchId: b.batch_id,
  modified: b.modified,
  truncated: b.truncated,
  processed: b.processed,
  skippedLocked: b.skipped_locked,
  from: b.date_from ?? null,
  resumeAfter: b.resume_after ?? null,
})

/**
 * Owner ruling 2026-10-15: a value the user set by hand is left alone UNLESS they explicitly ask to
 * change it. Asking is `overwrite_existing` ("Also change ones I set", or a ticked "You set this"),
 * so the server's hand-edit lock is respected exactly when the call does not overwrite.
 */
export const respectUserEdits = (overwrite: boolean) => !overwrite

/**
 * `POST /rules/{id}/apply`, synchronous, one batch. `respect_user_edits` is `!overwrite`
 * (`respectUserEdits`): a fill-only apply never writes a field edited by hand; an overwrite does.
 *
 * "Selected" names its transactions (the server still checks each against the conditions); a
 * window runs over `date_from` to today, capped at `APPLY_LIMIT`.
 */
export function useApplyRule(ruleId: string) {
  const client = useQueryClient()
  return useMutation<Applied, RulesError, ApplyCall>({
    mutationFn: async (call) => {
      const { data, error, response } = await api.POST('/api/v1/rules/{rule_id}/apply', {
        params: { path: { rule_id: ruleId } },
        body:
          call.kind === 'selected'
            ? { transaction_ids: call.transactionIds, limit: APPLY_LIMIT, overwrite_existing: call.overwrite, respect_user_edits: respectUserEdits(call.overwrite) }
            : {
                date_from: call.from,
                limit: APPLY_LIMIT,
                overwrite_existing: call.overwrite,
                respect_user_edits: respectUserEdits(call.overwrite),
                ...(call.resumeAfter === null ? {} : { resume_after: call.resumeAfter }),
              },
      })
      if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
      return appliedOf(data)
    },
    // rv-review M3: a rule deleted on another device answers 404. Refresh the list the page reads its
    // rule from, so it falls through to "no longer exists" rather than offering "Try again" forever.
    onError: (e) => (e.status === 404 ? ruleGone(client) : undefined),
    onSuccess: (result, call) => {
      emit('rule_applied', {
        method: call.kind === 'selected' ? 'selected' : 'apply_now',
        transaction_count: result.modified,
        truncation_shown: result.truncated,
        resume_used: call.kind === 'window' && call.resumeAfter !== null,
      })
      return invalidateAfterRuleWrite(client)
    },
  })
}

/** One run of Apply all rules. `atLeast` is the server's `truncated`; `resumeAfter` is the cursor
 *  where a run cut short stopped, passed back as `resume_after` to continue. Never `resume_date_to` as
 *  `date_to`: that loops when one date holds more rows than the limit (backend 521). */
export type AppliedAll = { batchId: string; modified: number; atLeast: boolean; resumeAfter: string | null }

/** A window for Apply all rules. `resumeAfter` is set only when continuing a run cut short. */
export type ApplyAllCall = { from: string; resumeAfter: string | null }

/**
 * `POST /rules/apply-all`: every enabled rule over `date_from` to today (or `date_to`), as one batch.
 * Fill-only and hand edits respected, server-side: the body has no overwrite to send. A concurrent run
 * is a 409, which the dialog words.
 */
export function useApplyAllRules() {
  const client = useQueryClient()
  return useMutation<AppliedAll, RulesError, ApplyAllCall>({
    mutationFn: async (call) => {
      const { data, error, response } = await api.POST('/api/v1/rules/apply-all', {
        body: { date_from: call.from, ...(call.resumeAfter === null ? {} : { resume_after: call.resumeAfter }), limit: APPLY_LIMIT },
      })
      if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
      return { batchId: data.batch_id, modified: data.modified, atLeast: data.truncated, resumeAfter: data.resume_after ?? null }
    },
    onSuccess: (result, call) => {
      emit('rule_applied', {
        method: 'apply_all',
        transaction_count: result.modified,
        truncation_shown: result.atLeast,
        resume_used: call.resumeAfter !== null,
      })
      return invalidateAfterRuleWrite(client)
    },
  })
}

/** Apply now's dry run, in the dialog's terms. */
export type ApplyPreview = {
  /** Would change. */
  count: number
  /** Matched in the window: everything the rule's conditions select, whatever it would do to them,
   *  less the ones removed from this rule. */
  matched: number
  /** Already hold the rule's value, or one a higher rule claims. */
  alreadyRight: number
  /** Hold a value the user set (by hand, or another value a fill-only apply leaves), and are left
   *  alone. Always 0 when overwriting: those are then in `count`, and in `overwriting`. */
  youSet: number
  /** Of `count`, the ones holding a value the user set, which change because they asked
   *  (`overwritten`). 0 on a fill-only run. */
  overwriting: number
  /** The server's `truncated`: the dry run stopped before the end of the window, after `reached`
   *  transactions. The apply walks the same newest rows and stops at the same place, so `count` is
   *  what it will change, not a floor. */
  truncated: boolean
  reached: number
}

/**
 * `GET /rules/{id}/apply/preview` for Apply now's window and overwrite choice: the exact count the
 * confirm states, with the same `respect_user_edits` as the apply it previews. `resumeAfter` counts
 * the stretch a Continue would apply (`resume_after`, a query param here); a malformed cursor is 422.
 *
 * With `overwrite` every value the user set, hand edits included, is "will change": the server then
 * reports them as `overwritten`, and nothing as `skipped_overwrites` or `skipped_locked`.
 */
export function useApplyPreview(ruleId: string, from: string, overwrite: boolean, resumeAfter: string | null, enabled: boolean) {
  const client = useQueryClient()
  const query = useQuery({
    queryKey: keys.rules.applyPreview(ruleId, { from, overwrite, resumeAfter }),
    queryFn: async ({ signal }): Promise<ApplyPreview> => {
      const { data, error, response } = await api.GET('/api/v1/rules/{rule_id}/apply/preview', {
        params: {
          path: { rule_id: ruleId },
          query: {
            overwrite_existing: overwrite,
            respect_user_edits: respectUserEdits(overwrite),
            date_from: from,
            limit: APPLY_LIMIT,
            sample_limit: 1,
            ...(resumeAfter === null ? {} : { resume_after: resumeAfter }),
          },
        },
        signal,
      })
      if (response.status === 404) void ruleGone(client)
      if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
      const c = data.counts
      const alreadyRight = c.already_correct + c.skipped_outranked
      const youSet = c.skipped_overwrites + c.skipped_locked
      const overwriting = overwrite ? c.overwritten : 0
      return {
        count: c.modified,
        // `processed` counts every row EVALUATED, matched or not, so the matches are the buckets'
        // sum. One removed from this rule (`skipped_excluded`) is not counted as a match.
        matched: c.modified + alreadyRight + youSet + c.skipped_inaccessible,
        alreadyRight,
        youSet,
        overwriting,
        truncated: c.truncated,
        reached: c.processed,
      }
    },
    enabled,
    // A count the user is about to act on: never one from an earlier visit.
    staleTime: 0,
  })
  return query
}

// ---- Exclusions: "Remove from rule" and the Removed tab ---------------------------------------

/** What Remove's revert did. `reverted` and `failed` count applications (log entries): fully undone,
 *  and refused because what it would put back is gone. `changed` and `locked` count FIELDS left: one
 *  that changed since the rule wrote it, or that the user set themselves. */
export type Removed = { reverted: number; failed: number; changed: number; locked: number }

/** Remove's outcome. `before` is the transaction as read just before the write, or `null` when that
 *  read failed: then the toast names no field it cannot tell. */
export type RemoveResult = { removed: Removed; before: Txn | null }

/**
 * `POST /rules/{id}/exclusions {transaction_ids: [id], revert: true}`: the transaction goes back the
 * way it was, and this rule stays off it for good. A field changed since the rule wrote it
 * (`skipped_changed`) or locked by a hand edit (`skipped_locked`) is left as it is.
 *
 * It first reads that one transaction (`GET /transactions/{id}`): Applied to's rows come from the
 * log's embedded summary, which carries no category, and the toast needs the field as it was before
 * the write to say what it stays as. One read per Remove, never one per row. A failed read does not
 * stop the Remove.
 */
export function useRemoveFromRule(ruleId: string) {
  const client = useQueryClient()
  return useMutation<RemoveResult, RulesError, string>({
    mutationFn: async (transactionId) => {
      const before = await getTransaction(transactionId).catch(() => null)
      const { data, error, response } = await api.POST('/api/v1/rules/{rule_id}/exclusions', {
        params: { path: { rule_id: ruleId } },
        body: { transaction_ids: [transactionId], revert: true },
      })
      if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
      const r = data.revert
      return {
        removed: { reverted: r?.reverted ?? 0, failed: r?.failed ?? 0, changed: r?.skipped_changed ?? 0, locked: r?.skipped_locked ?? 0 },
        before,
      }
    },
    onSuccess: () => {
      emit('rule_exclusion_added', { transaction_count: 1 })
      return invalidateAfterRuleWrite(client)
    },
  })
}

/** `DELETE /rules/{id}/exclusions/{txn}`: the rule MAY apply again. Nothing is re-applied here. */
export function usePutBack(ruleId: string) {
  const client = useQueryClient()
  return useMutation<void, RulesError, string>({
    mutationFn: async (transactionId) => {
      const { error, response } = await api.DELETE('/api/v1/rules/{rule_id}/exclusions/{transaction_id}', {
        params: { path: { rule_id: ruleId, transaction_id: transactionId } },
      })
      if (error !== undefined) throw new RulesError(error, response.status)
    },
    onSuccess: () => {
      emit('rule_exclusion_removed', { transaction_count: 1 })
      // The transaction itself is untouched: only the rule's own views move.
      return client.invalidateQueries({ queryKey: keys.rules.all() })
    },
  })
}

/** One transaction removed from the rule. */
export type RuleExclusion = { transactionId: string; createdAt: string }
export type RuleExclusionPage = { exclusions: RuleExclusion[]; total: number }

export const EXCLUSION_PAGE_SIZE = 25

type ExclusionList = components['schemas']['RuleExclusionList']

function flattenExclusions(data: InfiniteData<ExclusionList, number>): RuleExclusionPage {
  const seen = new Set<string>()
  const exclusions: RuleExclusion[] = []
  for (const p of data.pages) {
    for (const e of p.exclusions) {
      if (seen.has(e.transaction_id)) continue
      seen.add(e.transaction_id)
      exclusions.push({ transactionId: e.transaction_id, createdAt: e.created_at })
    }
  }
  return { exclusions, total: data.pages[0]?.total ?? 0 }
}

/** `GET /rules/{id}/exclusions`, newest first, paged. */
export function useRuleExclusions(ruleId: string): {
  state: AsyncState<RuleExclusionPage>
  hasMore: boolean
  loadingMore: boolean
  loadMore: () => void
} {
  const result = useInfiniteQuery({
    queryKey: keys.rules.exclusions(ruleId),
    queryFn: async ({ pageParam, signal }): Promise<ExclusionList> => {
      const { data, error, response } = await api.GET('/api/v1/rules/{rule_id}/exclusions', {
        params: { path: { rule_id: ruleId }, query: { page: pageParam, page_size: EXCLUSION_PAGE_SIZE } },
        signal,
      })
      if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
      return data
    },
    initialPageParam: 1,
    getNextPageParam: (last, _all, lastParam) => (last.has_more ? lastParam + 1 : undefined),
    select: flattenExclusions,
  })
  const state = fromQuery(result, {
    message: () => 'The transactions removed from this rule could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void result.refetch() },
    isEmpty: (d) => d.exclusions.length === 0,
  })
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = result
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  return { state, hasMore: hasNextPage, loadingMore: isFetchingNextPage, loadMore }
}
